import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { LateralJobPosition } from "@/types/database";

export interface LateralCandidateFormData {
  // Core
  full_name: string;
  email: string;
  phone: string;
  whatsapp_number: string;
  current_location: string;
  permanent_address: string;
  linkedin_url: string;
  github_url: string;
  lateral_job_position_id: string;
  notes: string;
  // Professional
  current_company: string;
  current_designation: string;
  total_experience_years: string;
  relevant_experience_years: string;
  current_ctc: string;
  in_hand_salary: string;
  expected_ctc: string;
  notice_period: string;
  key_skills: string;
  technologies: string;
  // AI-derived (admin-correctable)
  total_companies: string;
  avg_tenure_per_company: string;
  // Application answers (step 2)
  why_hire_you: string;
  reason_for_job_change: string;
  why_join_us: string;
  top_5_projects: string;
  // Education & interview
  college_name: string;
  highest_qualification: string;
  interview_preferred_date: string;
  interview_preferred_time: string;
}

export const emptyLateralCandidateForm = (): LateralCandidateFormData => ({
  full_name: "",
  email: "",
  phone: "",
  whatsapp_number: "",
  current_location: "",
  permanent_address: "",
  linkedin_url: "",
  github_url: "",
  lateral_job_position_id: "",
  notes: "",
  current_company: "",
  current_designation: "",
  total_experience_years: "",
  relevant_experience_years: "",
  current_ctc: "",
  in_hand_salary: "",
  expected_ctc: "",
  notice_period: "",
  key_skills: "",
  technologies: "",
  total_companies: "",
  avg_tenure_per_company: "",
  why_hire_you: "",
  reason_for_job_change: "",
  why_join_us: "",
  top_5_projects: "",
  college_name: "",
  highest_qualification: "",
  interview_preferred_date: "",
  interview_preferred_time: "",
});

interface Props {
  values: LateralCandidateFormData;
  onChange: (key: keyof LateralCandidateFormData, value: string) => void;
}

export function LateralCandidateForm({ values, onChange }: Props) {
  const field =
    (key: keyof LateralCandidateFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(key, e.target.value);

  const { data: positions } = useQuery({
    queryKey: ["lateral-job-positions-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lateral_job_positions")
        .select("id, job_title")
        .eq("is_active", true)
        .order("job_title");
      if (error) throw error;
      return data as Pick<LateralJobPosition, "id" | "job_title">[];
    },
  });

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <Label>Full Name *</Label>
            <Input value={values.full_name} onChange={field("full_name")} required />
          </div>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <Label>Email</Label>
            <Input type="email" value={values.email} onChange={field("email")} />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={values.phone} onChange={field("phone")} />
          </div>
          <div className="space-y-1.5">
            <Label>WhatsApp Number</Label>
            <Input value={values.whatsapp_number} onChange={field("whatsapp_number")} />
          </div>
          <div className="space-y-1.5">
            <Label>Current Location</Label>
            <Input value={values.current_location} onChange={field("current_location")} placeholder="City, State" />
          </div>
          <div className="space-y-1.5">
            <Label>LinkedIn</Label>
            <Input value={values.linkedin_url} onChange={field("linkedin_url")} />
          </div>
          <div className="space-y-1.5">
            <Label>GitHub</Label>
            <Input value={values.github_url} onChange={field("github_url")} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Permanent Address</Label>
            <Textarea value={values.permanent_address} onChange={field("permanent_address")} rows={2} />
          </div>
        </CardContent>
      </Card>

      {/* Professional Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Professional Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Current Company</Label>
            <Input value={values.current_company} onChange={field("current_company")} />
          </div>
          <div className="space-y-1.5">
            <Label>Current Designation</Label>
            <Input value={values.current_designation} onChange={field("current_designation")} />
          </div>
          <div className="space-y-1.5">
            <Label>Total Experience (years)</Label>
            <Input type="number" step="0.01" min="0" value={values.total_experience_years} onChange={field("total_experience_years")} />
          </div>
          <div className="space-y-1.5">
            <Label>Relevant Experience (years)</Label>
            <Input type="number" step="0.01" min="0" value={values.relevant_experience_years} onChange={field("relevant_experience_years")} />
          </div>
          <div className="space-y-1.5">
            <Label>Current CTC</Label>
            <Input value={values.current_ctc} onChange={field("current_ctc")} placeholder="e.g. 12 LPA" />
          </div>
          <div className="space-y-1.5">
            <Label>In-Hand Salary (monthly)</Label>
            <Input value={values.in_hand_salary} onChange={field("in_hand_salary")} placeholder="e.g. ₹80,000" />
          </div>
          <div className="space-y-1.5">
            <Label>Expected CTC</Label>
            <Input value={values.expected_ctc} onChange={field("expected_ctc")} placeholder="e.g. 18 LPA" />
          </div>
          <div className="space-y-1.5">
            <Label>Notice Period</Label>
            <Input value={values.notice_period} onChange={field("notice_period")} placeholder="e.g. 30 days" />
          </div>
          <div className="space-y-1.5">
            <Label>Total Companies Worked</Label>
            <Input type="number" min="0" value={values.total_companies} onChange={field("total_companies")} placeholder="AI-derived" />
          </div>
          <div className="space-y-1.5">
            <Label>Avg Tenure per Company (months)</Label>
            <Input type="number" step="0.1" min="0" value={values.avg_tenure_per_company} onChange={field("avg_tenure_per_company")} placeholder="AI-derived" />
          </div>
          <div className="space-y-1.5">
            <Label>Applying For</Label>
            <Select
              value={values.lateral_job_position_id}
              onValueChange={(v) => onChange("lateral_job_position_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select position" />
              </SelectTrigger>
              <SelectContent>
                {positions?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.job_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Key Skills (comma-separated)</Label>
            <Input value={values.key_skills} onChange={field("key_skills")} placeholder="React, Node.js, AWS..." />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Technologies (comma-separated)</Label>
            <Input value={values.technologies} onChange={field("technologies")} placeholder="PostgreSQL, Docker, Kubernetes..." />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Notes</Label>
            <Textarea value={values.notes} onChange={field("notes")} rows={3} />
          </div>
        </CardContent>
      </Card>

      {/* Application Answers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Application Answers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Why should we hire you?</Label>
            <Textarea value={values.why_hire_you} onChange={field("why_hire_you")} rows={3} />
          </div>
          <div className="space-y-1.5">
            <Label>Reason for job change</Label>
            <Textarea value={values.reason_for_job_change} onChange={field("reason_for_job_change")} rows={3} />
          </div>
          <div className="space-y-1.5">
            <Label>Why join us?</Label>
            <Textarea value={values.why_join_us} onChange={field("why_join_us")} rows={3} />
          </div>
          <div className="space-y-1.5">
            <Label>Top 5 Projects</Label>
            <Textarea value={values.top_5_projects} onChange={field("top_5_projects")} rows={4} placeholder="Describe your top projects..." />
          </div>
        </CardContent>
      </Card>

      {/* Education & Interview Preference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Education &amp; Interview Preference</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>College / University</Label>
            <Input value={values.college_name} onChange={field("college_name")} />
          </div>
          <div className="space-y-1.5">
            <Label>Highest Qualification</Label>
            <Input value={values.highest_qualification} onChange={field("highest_qualification")} placeholder="e.g. B.Tech, MBA" />
          </div>
          <div className="space-y-1.5">
            <Label>Preferred Interview Date</Label>
            <Input type="date" value={values.interview_preferred_date} onChange={field("interview_preferred_date")} />
          </div>
          <div className="space-y-1.5">
            <Label>Preferred Interview Time</Label>
            <Input value={values.interview_preferred_time} onChange={field("interview_preferred_time")} placeholder="e.g. 10:00 AM" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
